import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ActionIcon, Anchor, Divider, Menu, Table } from '@mantine/core';
import NumberFormat from 'react-number-format';
import dayjs from 'dayjs';
import {
  ArrowRight,
  CreditCard,
  DotsVertical,
  Edit,
  X
} from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import {
  CURRENCY_TYPE_LIST,
  WEBSITE_PAYMENT_STATUS_ENUM,
  WEBSITE_PAYMENT_STATUS_LIST
} from '../../../config/constants';
import RecordPaymentPriceItem from '../financials/RecordPaymentPriceItem';
import MenuContent from '../../common/MenuContent';
import { useModalState } from '../../../helpers/hooks';
import { formatLocalDate, formatUtcDate } from '../../../helpers/format';
import OrderPaymentInstallmentProcessConfirmModal from './OrderPaymentInstallmentProcessConfirmModal';
import OrderPaymentInstallmentEditModal from './OrderPaymentInstallmentEditModal';

const OrderPaymentInstallmentDetailsModal = ({
  regFormSubmissionPayment,
  pkRegCart,
  regCartBalance,
  isOpen,
  onClose,
  onRefreshData,
  showFullDetailsNav
}) => {
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const [formState, setFormState] = useState({
    paymentId: null,
    isLoading: false,
    isSubmitting: false,
    error: null
  });

  const paymentsWithInstallments = regFormSubmissionPayment?.regCartPayment
    ? [
        {
          entityId: regFormSubmissionPayment.regCartPayment.pkRegCartPayment,
          fkCurrencyType:
            regFormSubmissionPayment.regCartPayment.fkCurrencyType,
          amountInCents: regFormSubmissionPayment.regCartPayment.amount,
          installments: regFormSubmissionPayment.regCartPayment.regCartPaymentInstallments.map(
            (i) => ({
              entityId: i.pkRegCartPaymentInstallment,
              dueDate: i.dueDate,
              chargeAmount: i.chargeAmount,
              paymentStatus: i.fkWebsitePaymentStatus,
              refundAmount: i.refundAmount,
              attempts: (i.regCartPaymentInstallmentAttempts ?? []).map(
                (a) => ({
                  entityId: a.pkRegCartPaymentInstallmentAttempt,
                  succeeded: a.succeeded,
                  failureReason: a.failureReason,
                  createdAt: a.createdAt
                })
              )
            })
          )
        }
      ]
    : regCartBalance?.payments
        .filter((p) => p.installments.length > 0)
        .map((p) => ({
          ...p,
          installments: p.installments.map((i) => ({
            ...i,
            attempts: i.attempts ?? []
          }))
        })) ?? [];

  const selectedPayment = paymentsWithInstallments.find(
    (p) => p.entityId === formState.paymentId
  );

  const currency = CURRENCY_TYPE_LIST.find(
    (c) => c.value === selectedPayment?.fkCurrencyType
  );
  const currencyAbbr = currency?.abbr || null;

  const containsMoreThanOneLineItem =
    regFormSubmissionPayment?.amount !==
    (selectedPayment?.amountInCents ?? 0) / 100;

  const paymentDetails = selectedPayment?.installments.reduce(
    (r, c) => ({
      amountPaid:
        c.paymentStatus.toString() === WEBSITE_PAYMENT_STATUS_ENUM.PAID
          ? r.amountPaid + c.chargeAmount
          : r.amountPaid,
      amountScheduled:
        c.paymentStatus.toString() === WEBSITE_PAYMENT_STATUS_ENUM.SCHEDULED ||
        c.paymentStatus.toString() === WEBSITE_PAYMENT_STATUS_ENUM.PENDING
          ? r.amountScheduled + c.chargeAmount
          : r.amountScheduled,
      amountRefunded: r.amountRefunded + (c.refundAmount ?? 0)
    }),
    {
      amountPaid: 0,
      amountScheduled: 0,
      amountRefunded: 0
    }
  );

  const paymentAttempts = (selectedPayment?.installments ?? [])
    .reduce(
      (r, i) => [
        ...r,
        ...(i.attempts ?? []).map((a) => ({ ...a, dueDate: i.dueDate }))
      ],
      []
    )
    .sort(
      (a, b) =>
        new Date(formatUtcDate(b.createdAt)) -
        new Date(formatUtcDate(a.createdAt))
    );

  useEffect(() => {
    if (isOpen && (regCartBalance || regFormSubmissionPayment)) {
      setFormState({
        ...formState,
        paymentId: paymentsWithInstallments[0]?.entityId || null
      });
    }
  }, [isOpen, regCartBalance, regFormSubmissionPayment]);

  return (
    <ResponsiveModal
      formSectionProps={{
        cancelTitle: 'Close',
        onCancel: onClose,
        isSubmitHidden: true
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      size={600}
      title="Installment Details"
    >
      {!selectedPayment ? (
        <AppText
          style={{
            fontSize: 14,
            color: '#666',
            textAlign: 'center',
            fontStyle: 'italic'
          }}
        >
          No installment information is available for this payment.
        </AppText>
      ) : (
        <AppStack style={{ gap: 40 }}>
          {!regCartBalance && containsMoreThanOneLineItem && (
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              This installment is part of a larger order containing multiple
              line items. View the full order details to see all line items and
              their associated payments.
            </AppText>
          )}

          <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
            <AppText style={{ color: 'dodgerblue', fontSize: 18 }} weight={700}>
              Overview
            </AppText>

            <Divider size={2} />

            <RecordPaymentPriceItem
              currency={currencyAbbr}
              label="Payment Total"
              value={selectedPayment.amountInCents / 100}
              weight={700}
            />

            <RecordPaymentPriceItem
              color="#37b24d"
              currency={currencyAbbr}
              label="Amount Paid"
              value={paymentDetails.amountPaid / 100}
            />

            {paymentDetails.amountRefunded > 0 && (
              <RecordPaymentPriceItem
                color="#C40000"
                currency={currencyAbbr}
                label="Refunded Amount"
                value={(paymentDetails.amountRefunded / 100) * -1}
              />
            )}

            <RecordPaymentPriceItem
              currency={currencyAbbr}
              label="Remaining Amount"
              value={paymentDetails.amountScheduled / 100}
              weight={700}
            />

            <RecordPaymentPriceItem
              currency={currencyAbbr}
              isDividerHidden
              label="Balance"
              value={
                (selectedPayment.amountInCents -
                  paymentDetails.amountPaid -
                  paymentDetails.amountScheduled +
                  paymentDetails.amountRefunded) /
                100
              }
            />
          </AppStack>

          <Divider />

          <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
            <AppText style={{ color: 'dodgerblue', fontSize: 18 }} weight={700}>
              Installments
            </AppText>

            <Table withColumnBorders withRowBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'rgba(56, 56, 56, 0.1)'
                    }}
                  >
                    <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        Date
                      </AppText>
                    </AppFlexbox>
                  </Table.Th>

                  <Table.Th
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'rgba(56, 56, 56, 0.1)'
                    }}
                  >
                    <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        Amount
                      </AppText>
                    </AppFlexbox>
                  </Table.Th>

                  <Table.Th
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'rgba(56, 56, 56, 0.1)'
                    }}
                  >
                    <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        Status
                      </AppText>
                    </AppFlexbox>
                  </Table.Th>

                  {regCartBalance && (
                    <Table.Th
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'rgba(56, 56, 56, 0.1)'
                      }}
                    >
                      <AppFlexbox
                        style={{
                          gap: 16,
                          alignItems: 'center',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          Actions
                        </AppText>
                      </AppFlexbox>
                    </Table.Th>
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectedPayment.installments
                  .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                  .map((installment) => {
                    const status =
                      WEBSITE_PAYMENT_STATUS_LIST.find(
                        (s) => s.value === installment.paymentStatus.toString()
                      ) || {};

                    return (
                      <Table.Tr key={installment.entityId}>
                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {dayjs(formatLocalDate(installment.dueDate)).format(
                              'MMM D, YYYY'
                            )}
                          </AppText>
                        </Table.Td>

                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 'normal'
                            }}
                          >
                            <NumberFormat
                              decimalScale={2}
                              displayType="text"
                              fixedDecimalScale
                              prefix="$"
                              thousandSeparator
                              value={
                                (installment.chargeAmount -
                                  (installment.refundAmount ?? 0)) /
                                100
                              }
                            />
                          </AppText>
                        </Table.Td>

                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 'normal',
                              color: status?.color || '#000'
                            }}
                          >
                            {status?.label || 'Unknown'}
                          </AppText>
                        </Table.Td>

                        {regCartBalance && (
                          <Table.Td style={{ padding: '8px 16px' }}>
                            <AppFlexbox
                              style={{
                                gap: 5,
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {installment.paymentStatus.toString() !==
                                WEBSITE_PAYMENT_STATUS_ENUM.PAID && (
                                <MenuContent
                                  control={
                                    <ActionIcon color="dark" variant="subtle">
                                      <DotsVertical />
                                    </ActionIcon>
                                  }
                                >
                                  <Menu.Item
                                    leftSection={<CreditCard size={18} />}
                                    onClick={() => {
                                      onOpenModal('PROCESS_INSTALLMENT', {
                                        payment: selectedPayment,
                                        installment
                                      });
                                    }}
                                  >
                                    Process Payment
                                  </Menu.Item>
                                  <Menu.Item
                                    leftSection={<Edit size={18} />}
                                    onClick={() => {
                                      onOpenModal('EDIT_INSTALLMENT', {
                                        payment: selectedPayment,
                                        installment
                                      });
                                    }}
                                  >
                                    Edit Payment
                                  </Menu.Item>
                                </MenuContent>
                              )}
                            </AppFlexbox>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    );
                  })}
              </Table.Tbody>
            </Table>
          </AppStack>

          {paymentAttempts.length > 0 && (
            <>
              <Divider />

              <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
                <AppText
                  style={{ color: 'dodgerblue', fontSize: 18 }}
                  weight={700}
                >
                  Attempt History
                </AppText>

                <Table withColumnBorders withRowBorders withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'rgba(56, 56, 56, 0.1)'
                        }}
                      >
                        <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Date
                          </AppText>
                        </AppFlexbox>
                      </Table.Th>

                      <Table.Th
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'rgba(56, 56, 56, 0.1)'
                        }}
                      >
                        <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Installment
                          </AppText>
                        </AppFlexbox>
                      </Table.Th>

                      <Table.Th
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'rgba(56, 56, 56, 0.1)'
                        }}
                      >
                        <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Result
                          </AppText>
                        </AppFlexbox>
                      </Table.Th>

                      <Table.Th
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'rgba(56, 56, 56, 0.1)'
                        }}
                      >
                        <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Details
                          </AppText>
                        </AppFlexbox>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paymentAttempts.map((attempt) => (
                      <Table.Tr key={attempt.entityId}>
                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {dayjs(formatUtcDate(attempt.createdAt)).format(
                              'MMM D, YYYY h:mm A'
                            )}
                          </AppText>
                        </Table.Td>

                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 'normal'
                            }}
                          >
                            {dayjs(formatLocalDate(attempt.dueDate)).format(
                              'MMM D, YYYY'
                            )}
                          </AppText>
                        </Table.Td>

                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 'normal',
                              color: attempt.succeeded ? '#37b24d' : '#C40000'
                            }}
                          >
                            {attempt.succeeded ? 'Succeeded' : 'Failed'}
                          </AppText>
                        </Table.Td>

                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 'normal'
                            }}
                          >
                            {attempt.failureReason || '—'}
                          </AppText>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </AppStack>
            </>
          )}

          {regFormSubmissionPayment && pkRegCart && showFullDetailsNav && (
            <AppFlexbox
              style={{ alignItems: 'flex-end', justifyContent: 'flex-end' }}
            >
              <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                <Anchor
                  component={Link}
                  style={{ fontSize: 14, color: 'dodgerblue' }}
                  to={`/admin/financials/orders/${pkRegCart}?from=registrations&fromId=${regFormSubmissionPayment?.fkRegFormSubmission}&modal=installments`}
                >
                  View full details
                </Anchor>
                <ArrowRight color="dodgerblue" size={14} />
              </AppFlexbox>
            </AppFlexbox>
          )}
        </AppStack>
      )}

      <OrderPaymentInstallmentProcessConfirmModal
        installment={
          modalState.action === 'PROCESS_INSTALLMENT'
            ? modalState.item?.installment
            : null
        }
        isOpen={
          modalState.isOpen && modalState.action === 'PROCESS_INSTALLMENT'
        }
        onClose={onCloseModal}
        onRefreshData={onRefreshData}
        payment={
          modalState.action === 'PROCESS_INSTALLMENT'
            ? modalState.item?.payment
            : null
        }
        pkRegCart={pkRegCart}
      />

      <OrderPaymentInstallmentEditModal
        installment={
          modalState.action === 'EDIT_INSTALLMENT'
            ? modalState.item?.installment
            : null
        }
        isOpen={modalState.isOpen && modalState.action === 'EDIT_INSTALLMENT'}
        onClose={onCloseModal}
        payment={
          modalState.action === 'EDIT_INSTALLMENT'
            ? modalState.item?.payment
            : null
        }
        pkRegCart={pkRegCart}
      />
    </ResponsiveModal>
  );
};

OrderPaymentInstallmentDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRefreshData: PropTypes.func,
  pkRegCart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  regCartBalance: PropTypes.object,
  regFormSubmissionPayment: PropTypes.object,
  showFullDetailsNav: PropTypes.bool
};

export default OrderPaymentInstallmentDetailsModal;
